import React, { useState, useEffect } from "react";
import Loader from "./components/loader";
import ErrorBoundary from "./components/ErrorBoundary";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/footer";
import { auth } from "./firebase/config";
import { AuthProvider } from "./contexts/AuthContext";
import {
  getUserCart,
  getUserWishlist,
  saveUserCart,
  saveUserWishlist,
} from "./firebase/realtimeDb";


import Home from "./pages/home";
import About from "./pages/about";
import Category from "./pages/category";
import Shop from "./pages/shop";
import Contact from "./pages/contact";
import Login from "./pages/login";
import SignupForm from "./pages/signup";
import ShoppingCart from "./pages/ShoppingCart";
import Dashboard from "./pages/dashboard";
import Wishlist from "./pages/wishlist";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/checkout";
import PaymentSuccess from "./pages/PaymentSuccess";
import AdminLogin from "./pages/AdminLogin";
import AdminRegister from "./pages/AdminRegister";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminOrders from "./pages/AdminOrders";
import AdminProductImages from "./pages/AdminProductImages";
import AdminProducts from "./pages/AdminProducts";
import AdminOffers from "./pages/AdminOffers";
import AdminSettings from "./pages/AdminSettings";
import AdminGuard from "./components/AdminGuard";


function mergeCarts(firebaseCart, localCart) {
  const merged = [...firebaseCart];
  localCart.forEach((localItem) => {
    const idx = merged.findIndex((item) => item.id === localItem.id);
    if (idx !== -1) {
      // Sum quantities
      merged[idx].quantity =
        (merged[idx].quantity || 1) + (localItem.quantity || 1);
    } else {
      merged.push(localItem);
    }
  });
  return merged;
}

function mergeWishlists(firebaseWishlist, localWishlist) {
  const ids = new Set(firebaseWishlist.map((item) => item.id));
  return [
    ...firebaseWishlist,
    ...localWishlist.filter((item) => !ids.has(item.id)),
  ];
}

function App() {
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(null);

  // Listen for auth state changes and load cart/wishlist from Firebase or localStorage
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        setLoading(true);
        let [cartData, wishlistData] = await Promise.all([
          getUserCart(user.uid),
          getUserWishlist(user.uid),
        ]);
        // Check for localStorage guest cart/wishlist
        let mergedCart = cartData;
        let mergedWishlist = wishlistData;
        let merged = false;
        const localCartRaw = localStorage.getItem("guestCart");
        const localWishlistRaw = localStorage.getItem("guestWishlist");
        if (localCartRaw) {
          try {
            const localCart = JSON.parse(localCartRaw);
            if (Array.isArray(localCart) && localCart.length > 0) {
              mergedCart = mergeCarts(cartData, localCart);
              merged = true;
            }
          } catch {}
        }
        if (localWishlistRaw) {
          try {
            const localWishlist = JSON.parse(localWishlistRaw);
            if (Array.isArray(localWishlist) && localWishlist.length > 0) {
              mergedWishlist = mergeWishlists(wishlistData, localWishlist);
              merged = true;
            }
          } catch {}
        }
        if (merged) {
          await Promise.all([
            saveUserCart(user.uid, mergedCart),
            saveUserWishlist(user.uid, mergedWishlist),
          ]);
          localStorage.removeItem("guestCart");
          localStorage.removeItem("guestWishlist");
        }
        setCart(mergedCart);
        setWishlist(mergedWishlist);
        setLoading(false);
      } else {
        // Load guest cart/wishlist from localStorage
        const guestCartRaw = localStorage.getItem("guestCart");
        const guestWishlistRaw = localStorage.getItem("guestWishlist");
        let guestCart = [];
        let guestWishlist = [];

        if (guestCartRaw) {
          try {
            guestCart = JSON.parse(guestCartRaw);
          } catch {}
        }
        if (guestWishlistRaw) {
          try {
            guestWishlist = JSON.parse(guestWishlistRaw);
          } catch {}
        }

        setCart(guestCart);
        setWishlist(guestWishlist);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Save cart to Firebase or localStorage whenever it changes
  useEffect(() => {
    if (user && cart.length > 0) {
      saveUserCart(user.uid, cart);
    } else if (user && cart.length === 0 && window.__explicitlyClearedCart) {
      // Only save empty cart if user explicitly cleared it
      saveUserCart(user.uid, []);
      window.__explicitlyClearedCart = false;
    } else if (!user) {
      // Save to localStorage for guest users
      localStorage.setItem("guestCart", JSON.stringify(cart));
    }
    // Always update global cart for checkout page
    window.cart = cart;
  }, [cart, user]);

  // Save wishlist to Firebase or localStorage whenever it changes
  useEffect(() => {
    if (user && wishlist.length > 0) {
      saveUserWishlist(user.uid, wishlist);
    } else if (
      user &&
      wishlist.length === 0 &&
      window.__explicitlyClearedWishlist
    ) {
      // Only save empty wishlist if user explicitly cleared it
      saveUserWishlist(user.uid, []);
      window.__explicitlyClearedWishlist = false;
    } else if (!user) {
      // Save to localStorage for guest users
      localStorage.setItem("guestWishlist", JSON.stringify(wishlist));
    }
  }, [wishlist, user]);

  const addToCart = (product) => {
    // Validate product data
    if (!product || !product.id || !product.name || !product.price) {
      return;
    }

    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });
  };

  const incrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQuantity = (id) => {
    setCart((prevCart) =>
      prevCart.reduce((acc, item) => {
        if (item.id === id) {
          if (item.quantity > 1) {
            acc.push({ ...item, quantity: item.quantity - 1 });
          }
        } else {
          acc.push(item);
        }
        return acc;
      }, [])
    );
  };

  const removeFromCart = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  const addToWishlist = (product) => {
    // Validate product data
    if (!product || !product.id || !product.name || !product.price) {
      return;
    }

    setWishlist((prevWishlist) => {
      if (!prevWishlist.find((item) => item.id === product.id)) {
        return [...prevWishlist, product];
      }
      return prevWishlist;
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist((prevWishlist) =>
      prevWishlist.filter((item) => item.id !== productId)
    );
  };

  // Function to clear cart after successful payment
  const clearCart = () => {
    setCart([]);
    window.__explicitlyClearedCart = true;
    if (user) {
      saveUserCart(user.uid, []);
    } else {
      localStorage.removeItem("guestCart");
    }
  };

  if (loading) {
    return <Loader />;
  }



  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent 
            cartItems={cart} 
            wishlist={wishlist}
            addToCart={addToCart}
            addToWishlist={addToWishlist}
            removeFromWishlist={removeFromWishlist}
            removeFromCart={removeFromCart}
            incrementQuantity={incrementQuantity}
            decrementQuantity={decrementQuantity}
            clearCart={clearCart}
          />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Wrapper component to conditionally render Header and Footer
function AppContent({ 
  cartItems, 
  wishlist, 
  addToCart, 
  addToWishlist, 
  removeFromWishlist, 
  removeFromCart, 
  incrementQuantity, 
  decrementQuantity, 
  clearCart 
}) {
  const location = useLocation();
  
  // Check if current route is admin-related
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isAdminRoute && <Header cartItems={cartItems} wishlist={wishlist} />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/categories" element={<Category />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignupForm />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/orders" element={<Navigate to="/dashboard?tab=orders" replace />} />
          <Route path="/profile" element={<Navigate to="/dashboard?tab=profile" replace />} />
          <Route path="/settings" element={<Navigate to="/dashboard?tab=settings" replace />} />
          <Route
            path="/shop"
            element={
              <Shop
                addToCart={addToCart}
                wishlist={wishlist}
                addToWishlist={addToWishlist}
                removeFromWishlist={removeFromWishlist}
              />
            }
          />
          <Route
            path="/shop/:category"
            element={
              <Shop
                addToCart={addToCart}
                wishlist={wishlist}
                addToWishlist={addToWishlist}
                removeFromWishlist={removeFromWishlist}
              />
            }
          />
          <Route
            path="/cart"
            element={
              <ShoppingCart
                cartItems={cartItems}
                onRemove={removeFromCart}
                onQuantityChange={(id, action) => {
                  if (action === "increase") {
                    incrementQuantity(id);
                  } else {
                    decrementQuantity(id);
                  }
                }}
                clearCart={clearCart}
              />
            }
          />
          <Route
            path="/product/:id"
            element={
              <ProductDetail
                addToCart={addToCart}
                wishlist={wishlist}
                addToWishlist={addToWishlist}
                removeFromWishlist={removeFromWishlist}
              />
            }
          />
          <Route path="/checkout" element={<Checkout />} />
          <Route
            path="/wishlist"
            element={
              <Wishlist
                wishlist={wishlist}
                onRemoveFromWishlist={removeFromWishlist}
                onAddToCart={addToCart}
              />
            }
          />
          <Route path="/payment-success" element={<PaymentSuccess clearCart={clearCart} />} />
          

          
          {/* Admin Routes */}
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin-register" element={<AdminRegister />} />
          <Route 
            path="/admin-dashboard" 
            element={
              <AdminGuard>
                <AdminDashboard />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-dashboard/*" 
            element={
              <AdminGuard>
                <AdminDashboard />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-users" 
            element={
              <AdminGuard>
                <AdminUsers />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-orders" 
            element={
              <AdminGuard>
                <AdminOrders />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-product-images" 
            element={
              <AdminGuard>
                <AdminProductImages />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-products" 
            element={
              <AdminGuard>
                <AdminProducts />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-offers" 
            element={
              <AdminGuard>
                <AdminOffers />
              </AdminGuard>
            } 
          />
          <Route 
            path="/admin-settings" 
            element={
              <AdminGuard>
                <AdminSettings />
              </AdminGuard>
            } 
          />
        </Routes>
      </main>
        {!isAdminRoute && <Footer />}
      </>
    );
  }

export default App;
